/* EnterpriseEnquiryModal.jsx */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import '../styles/EnterpriseEnquiryModal.css';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';

const INITIAL_FORM_DATA = {
  companyName: '',
  contactName: '',
  contactEmail: '',
  teamSize: '',
  customBrandedCards: 'yes',
  customBrandedCardsDetail: '',
  adminLicences: '',
  marketIntelligenceTools: 'yes',
  eventIdentificationTools: 'yes',
  trackingInterests: '',
  comments: ''
};

/**
 * A solo professional ordering their own custom card answers the same enquiry,
 * minus the questions that only make sense for an organisation. Seats are
 * pre-set to one rather than asked for, and the team step is dropped, so the
 * existing /api/enterprise/enquiry payload stays unchanged.
 */
const SOLO_DEFAULTS = { teamSize: '1', adminLicences: '1' };

const buildSteps = (audience) => {
  const isSolo = audience === 'professional';

  return [
    {
      id: 'intro',
      label: 'Introduction',
      heading: isSolo ? "Let's design your card" : "Let's understand your team",
      description: isSolo
        ? 'Share a few details and our design team will come back with a proof and a price. Takes about a minute.'
        : 'Share a few details and our enterprise team will prepare a tailored proposal. Takes about two minutes.'
    },
    {
      id: 'contact',
      label: 'Contact details',
      heading: 'How can we reach you?',
      description: isSolo
        ? 'Your name, email and the company you want on the card.'
        : 'Your name, email, and company so we can follow up personally.'
    },
    ...(isSolo
      ? []
      : [
          {
            id: 'team',
            label: 'Team size',
            heading: 'How large is your team?',
            description: 'Help us understand the scale of your deployment.'
          }
        ]),
    {
      id: 'branded',
      label: isSolo ? 'Card design' : 'Branded cards',
      heading: isSolo ? 'Your card design' : 'Custom branded metal cards',
      description: isSolo
        ? 'Premium metal contactless cards, designed with you or from your own artwork.'
        : 'Premium contactless cards customised to your brand.'
    },
    {
      id: 'insights',
      label: 'Tools',
      heading: 'Intelligence & events',
      description: isSolo
        ? 'Tell us which Velox tools may be relevant to you.'
        : 'Tell us which Velox tools may be relevant for your team.'
    },
    {
      id: 'requirements',
      label: 'Requirements',
      heading: isSolo ? 'Anything else?' : 'Tracking & notes',
      description: 'Optional. Share KPI interests or any other context.'
    },
    {
      id: 'review',
      label: 'Review',
      heading: 'Review your enquiry',
      description: 'Please confirm your details before submitting.'
    }
  ];
};

/** Maps internal form state to the existing API payload shape. */
const buildApiPayload = (formData) => ({
  companyName: formData.companyName,
  contactName: formData.contactName,
  contactEmail: formData.contactEmail,
  teamSize: formData.teamSize,
  customBrandedCards: formData.customBrandedCards,
  customBrandedCardsDetail: formData.customBrandedCardsDetail,
  adminLicences: formData.adminLicences,
  trackingInterests: formData.trackingInterests,
  weeklyInsights: formData.marketIntelligenceTools,
  weeklyInsightsDetail:
    formData.eventIdentificationTools === 'yes' ? 'Event identification tools: Yes' : '',
  comments: formData.comments
});

const validateField = (name, value) => {
  switch (name) {
    case 'companyName':
      if (!value.trim()) return 'Company name is required';
      return '';
    case 'contactName':
      if (!value.trim()) return 'Contact name is required';
      return '';
    case 'contactEmail':
      if (!value.trim()) return 'Contact email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
      return '';
    case 'teamSize':
      if (!value.trim()) return 'Team size is required';
      if (isNaN(value) || parseInt(value, 10) < 1) return 'Please enter a valid number';
      return '';
    case 'adminLicences':
      if (!value.trim()) return 'Number of admin licences is required';
      if (isNaN(value) || parseInt(value, 10) < 1) return 'Please enter a valid number';
      return '';
    default:
      return '';
  }
};

const validateAll = (formData) => {
  const required = ['companyName', 'contactName', 'contactEmail', 'teamSize', 'adminLicences'];
  const newErrors = {};
  required.forEach((field) => {
    const err = validateField(field, formData[field]);
    if (err) newErrors[field] = err;
  });
  return newErrors;
};

const STEP_FIELDS = {
  contact: ['contactName', 'contactEmail', 'companyName'],
  team: ['teamSize', 'adminLicences']
};

const formatYesNo = (value) => (value === 'yes' ? 'Yes' : 'No');

const EnterpriseEnquiryModal = ({ onClose, audience = 'team' }) => {
  const { showSuccess, showError } = useToast();
  const isSolo = audience === 'professional';
  const STEPS = useMemo(() => buildSteps(audience), [audience]);
  const TOTAL_STEPS = STEPS.length;
  const [formData, setFormData] = useState(() =>
    isSolo ? { ...INITIAL_FORM_DATA, ...SOLO_DEFAULTS } : INITIAL_FORM_DATA
  );
  const [errors, setErrors] = useState({});
  const [stepError, setStepError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [direction, setDirection] = useState('forward');
  const stepBodyRef = useRef(null);
  const liveRegionRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
    if (stepError) setStepError('');
  };

  const validateCurrentStep = useCallback(() => {
    const stepId = STEPS[currentStep].id;
    if (stepId === 'intro' || stepId === 'branded' || stepId === 'insights' || stepId === 'requirements' || stepId === 'review') {
      return true;
    }

    const fields = STEP_FIELDS[stepId] || [];
    const stepErrors = {};
    fields.forEach((field) => {
      const err = validateField(field, formData[field]);
      if (err) stepErrors[field] = err;
    });

    if (Object.keys(stepErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      setStepError('Please complete the required fields to continue.');
      return false;
    }

    setStepError('');
    return true;
  }, [currentStep, formData, STEPS]);

  const goNext = () => {
    if (!validateCurrentStep()) return;
    setDirection('forward');
    setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const goBack = () => {
    setStepError('');
    setDirection('backward');
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allErrors = validateAll(formData);
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      setStepError('Please review the highlighted fields.');
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    setStepError('');

    try {
      await api.post('/api/enterprise/enquiry', buildApiPayload(formData));
      setIsSuccess(true);
      showSuccess("Thank you! We'll get back to you shortly.");
      setTimeout(() => {
        onClose();
      }, 2500);
    } catch (error) {
      console.error('Enterprise enquiry error:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to submit enquiry. Please try again later.';
      setStepError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, isSubmitting]);

  useEffect(() => {
    if (stepBodyRef.current) {
      stepBodyRef.current.scrollTop = 0;
    }

    if (stepBodyRef.current) {
      const stepId = STEPS[currentStep]?.id;
      if (stepId === 'intro' || stepId === 'review' || isSuccess) return;

      const focusable = stepBodyRef.current.querySelector(
        'input, textarea, button.enquiry-yesno-option:not([disabled])'
      );
      if (focusable) focusable.focus({ preventScroll: true });
    }
  }, [currentStep, isSuccess, STEPS]);

  useEffect(() => {
    if (liveRegionRef.current && stepError) {
      liveRegionRef.current.textContent = stepError;
    }
  }, [stepError]);

  const progressPercent = ((currentStep + 1) / TOTAL_STEPS) * 100;
  const stepMeta = STEPS[currentStep];
  const isReviewStep = stepMeta.id === 'review';
  const isLastContentStep = currentStep === TOTAL_STEPS - 1;

  const handleFormKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey || isSubmitting || isSuccess) return;
    if (e.target.tagName === 'TEXTAREA') return;

    e.preventDefault();
    if (isReviewStep) {
      handleSubmit(e);
    } else {
      goNext();
    }
  };

  const YesNoToggle = ({ name, value, onChange, id }) => (
    <div className="enquiry-yesno" role="group" aria-labelledby={id}>
      {['no', 'yes'].map((option) => (
        <button
          key={option}
          type="button"
          className={`enquiry-yesno-option${value === option ? ' is-selected' : ''}`}
          aria-pressed={value === option}
          onClick={() => onChange({ target: { name, value: option } })}
        >
          {option === 'yes' ? 'Yes' : 'No'}
        </button>
      ))}
    </div>
  );

  const renderReviewValue = (label, value, { multiline = false, hideIfEmpty = false } = {}) => {
    if (hideIfEmpty && !value?.trim()) return null;
    const display = value?.trim() ? value : '—';
    return (
      <div className="enquiry-review-row">
        <dt>{label}</dt>
        <dd className={multiline ? 'enquiry-review-multiline' : ''}>{display}</dd>
      </div>
    );
  };

  const renderReviewSection = (title, children) => (
    <div className="enquiry-review-section">
      <h4 className="enquiry-review-section-title">{title}</h4>
      <dl className="enquiry-review-list">{children}</dl>
    </div>
  );

  const renderStepContent = () => {
    if (isSuccess) {
      return (
        <div className="enquiry-step enquiry-step-success">
          <div className="enquiry-success-icon" aria-hidden="true">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 12.5l2.5 2.5L16 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="enquiry-step-description">
            {isSolo
              ? 'Thank you. Our design team will be in touch shortly with a proof and a price.'
              : 'Thank you for your interest in Velox Enterprise. Our team will be in touch shortly.'}
          </p>
        </div>
      );
    }

    switch (stepMeta.id) {
      case 'intro':
        return (
          <div className={`enquiry-step enquiry-step-${direction}`}>
            <h3 className="enquiry-step-heading">{stepMeta.heading}</h3>
            <p className="enquiry-step-description">{stepMeta.description}</p>
            <ul className="enquiry-intro-list" aria-label="What you will provide">
              <li>Contact and company details</li>
              {!isSolo && <li>Team size and admin access</li>}
              <li>{isSolo ? 'Your design requirements' : 'Branding and analytics preferences'}</li>
            </ul>
          </div>
        );

      case 'contact':
        return (
          <div className={`enquiry-step enquiry-step-${direction}`}>
            <h3 className="enquiry-step-heading">{stepMeta.heading}</h3>
            <p className="enquiry-step-description">{stepMeta.description}</p>
            <div className="form-group form-group-compact">
              <label htmlFor="contactName">
                Contact Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="contactName"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                className={errors.contactName ? 'error' : ''}
                autoComplete="name"
              />
              {errors.contactName && <span className="error-message">{errors.contactName}</span>}
            </div>
            <div className="form-group form-group-compact">
              <label htmlFor="contactEmail">
                Contact Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="contactEmail"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                className={errors.contactEmail ? 'error' : ''}
                autoComplete="email"
                inputMode="email"
              />
              {errors.contactEmail && <span className="error-message">{errors.contactEmail}</span>}
            </div>
            <div className="form-group form-group-compact">
              <label htmlFor="companyName">
                {isSolo ? 'Company or organisation' : 'Company Name'}{' '}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className={errors.companyName ? 'error' : ''}
                autoComplete="organization"
              />
              {errors.companyName && <span className="error-message">{errors.companyName}</span>}
            </div>
          </div>
        );

      case 'team':
        return (
          <div className={`enquiry-step enquiry-step-${direction}`}>
            <h3 className="enquiry-step-heading">{stepMeta.heading}</h3>
            <p className="enquiry-step-description">{stepMeta.description}</p>
            <div className="form-group form-group-compact">
              <label htmlFor="teamSize">
                Number of Team Members <span className="required">*</span>
              </label>
              <input
                type="number"
                id="teamSize"
                name="teamSize"
                value={formData.teamSize}
                onChange={handleChange}
                min="1"
                className={errors.teamSize ? 'error' : ''}
                inputMode="numeric"
              />
              {errors.teamSize && <span className="error-message">{errors.teamSize}</span>}
            </div>
            <div className="form-group form-group-compact">
              <label htmlFor="adminLicences">
                Number of Admin Licences <span className="required">*</span>
              </label>
              <input
                type="number"
                id="adminLicences"
                name="adminLicences"
                value={formData.adminLicences}
                onChange={handleChange}
                min="1"
                className={errors.adminLicences ? 'error' : ''}
                inputMode="numeric"
              />
              <small>Admin logins for the analytics &amp; performance dashboard</small>
              {errors.adminLicences && <span className="error-message">{errors.adminLicences}</span>}
            </div>
          </div>
        );

      case 'branded':
        return (
          <div className={`enquiry-step enquiry-step-${direction}`}>
            <h3 className="enquiry-step-heading">{stepMeta.heading}</h3>
            <p className="enquiry-step-description">{stepMeta.description}</p>
            <div className="form-group form-group-compact">
              <span className="enquiry-field-label" id="customBrandedCards-label">
                {isSolo
                  ? 'Do you want a custom design rather than a standard card?'
                  : 'Custom branded Velox metal business cards?'}
              </span>
              <YesNoToggle
                id="customBrandedCards-label"
                name="customBrandedCards"
                value={formData.customBrandedCards}
                onChange={handleChange}
              />
              {formData.customBrandedCards === 'yes' && (
                <>
                  <label htmlFor="customBrandedCardsDetail" className="enquiry-optional-label">
                    {isSolo ? 'Design requirements' : 'Brand requirements'}{' '}
                    <span className="optional-hint">(optional)</span>
                  </label>
                  <textarea
                    id="customBrandedCardsDetail"
                    name="customBrandedCardsDetail"
                    value={formData.customBrandedCardsDetail}
                    onChange={handleChange}
                    placeholder={
                      isSolo
                        ? 'Optional: finish, engraving, whether you have your own artwork...'
                        : 'Optional: logo, finish, quantities, or other branding notes...'
                    }
                    rows="2"
                    className="enquiry-textarea-compact"
                    aria-label="Brand requirements"
                  />
                </>
              )}
            </div>
          </div>
        );

      case 'insights':
        return (
          <div className={`enquiry-step enquiry-step-${direction}`}>
            <h3 className="enquiry-step-heading">{stepMeta.heading}</h3>
            <p className="enquiry-step-description">{stepMeta.description}</p>
            <div className="form-group form-group-compact">
              <span className="enquiry-field-label" id="marketIntelligenceTools-label">
                Interested in market intelligence tools?
              </span>
              <YesNoToggle
                id="marketIntelligenceTools-label"
                name="marketIntelligenceTools"
                value={formData.marketIntelligenceTools}
                onChange={handleChange}
              />
            </div>
            <div className="form-group form-group-compact">
              <span className="enquiry-field-label" id="eventIdentificationTools-label">
                Interested in event identification tools?
              </span>
              <YesNoToggle
                id="eventIdentificationTools-label"
                name="eventIdentificationTools"
                value={formData.eventIdentificationTools}
                onChange={handleChange}
              />
            </div>
          </div>
        );

      case 'requirements':
        return (
          <div className={`enquiry-step enquiry-step-${direction}`}>
            <h3 className="enquiry-step-heading">{stepMeta.heading}</h3>
            <p className="enquiry-step-description">{stepMeta.description}</p>
            <div className="form-group form-group-compact">
              <label htmlFor="trackingInterests">
                {isSolo
                  ? 'What would you most like Velox to help you track?'
                  : 'What are you interested in tracking your team on? (KPIs, metrics, etc.)'}
              </label>
              <textarea
                id="trackingInterests"
                name="trackingInterests"
                value={formData.trackingInterests}
                onChange={handleChange}
                placeholder="e.g., Contacts created, events attended, follow-up rates..."
                rows="2"
                className="enquiry-textarea-compact"
              />
            </div>
            <div className="form-group form-group-compact">
              <label htmlFor="comments">Additional comments</label>
              <textarea
                id="comments"
                name="comments"
                value={formData.comments}
                onChange={handleChange}
                placeholder="Any other requirements or context..."
                rows="2"
                className="enquiry-textarea-compact"
              />
            </div>
          </div>
        );

      case 'review':
        return (
          <div className={`enquiry-step enquiry-step-review enquiry-step-${direction}`}>
            <h3 className="enquiry-step-heading">{stepMeta.heading}</h3>
            <p className="enquiry-step-description">{stepMeta.description}</p>
            <div className="enquiry-review-wrap">
              {renderReviewSection(
                'Contact',
                <>
                  {renderReviewValue('Name', formData.contactName)}
                  {renderReviewValue('Email', formData.contactEmail)}
                  {renderReviewValue('Company', formData.companyName)}
                </>
              )}
              {!isSolo &&
                renderReviewSection(
                  'Team',
                  <>
                    {renderReviewValue('Team members', formData.teamSize)}
                    {renderReviewValue('Admin licences', formData.adminLicences)}
                  </>
                )}
              {renderReviewSection(
                'Preferences',
                <>
                  {renderReviewValue(
                    isSolo ? 'Custom design' : 'Custom branded cards',
                    formatYesNo(formData.customBrandedCards)
                  )}
                  {formData.customBrandedCards === 'yes' &&
                    renderReviewValue(
                      isSolo ? 'Design requirements' : 'Brand requirements',
                      formData.customBrandedCardsDetail,
                      { multiline: true, hideIfEmpty: true }
                    )}
                  {renderReviewValue(
                    'Market intelligence tools',
                    formatYesNo(formData.marketIntelligenceTools)
                  )}
                  {renderReviewValue(
                    'Event identification tools',
                    formatYesNo(formData.eventIdentificationTools)
                  )}
                  {renderReviewValue('Tracking interests', formData.trackingInterests, {
                    multiline: true,
                    hideIfEmpty: true
                  })}
                  {renderReviewValue('Additional comments', formData.comments, {
                    multiline: true,
                    hideIfEmpty: true
                  })}
                </>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const modalContent = (
    <div
      className="enterprise-modal-overlay"
      onClick={isSubmitting || isSuccess ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="enterprise-modal-title"
    >
      <div className="enterprise-modal enterprise-modal-stepper" onClick={(e) => e.stopPropagation()}>
        <div
          className={`enterprise-modal-header${
            isSuccess ? ' enterprise-modal-header-success' : ' enterprise-modal-header-stepper'
          }`}
        >
          <h2 id="enterprise-modal-title">
            {isSuccess
              ? 'Enquiry received'
              : isSolo
                ? 'Custom card enquiry'
                : 'Custom cards for your team'}
          </h2>
          {!isSuccess && (
            <button
              className="enterprise-modal-close"
              onClick={onClose}
              aria-label="Close modal"
              disabled={isSubmitting}
              type="button"
            >
              ×
            </button>
          )}
        </div>

        <div ref={liveRegionRef} className="sr-only" aria-live="polite" aria-atomic="true" />

        <form
          className="enterprise-modal-form"
          onSubmit={isReviewStep ? handleSubmit : (e) => e.preventDefault()}
          onKeyDown={handleFormKeyDown}
        >
          <div
            className={`enterprise-modal-step-body${isReviewStep ? ' is-scrollable' : ''}`}
            ref={stepBodyRef}
          >
            {renderStepContent()}
          </div>

          {!isSuccess && (
            <div className="enquiry-step-footer">
              {stepError && (
                <div className="enterprise-modal-step-error" role="alert">
                  {stepError}
                </div>
              )}

              <div className="enterprise-modal-progress">
                <div className="enterprise-modal-progress-meta">
                  <span className="enterprise-modal-step-label">
                    Step {currentStep + 1} of {TOTAL_STEPS}
                  </span>
                </div>
                <div
                  className="enterprise-modal-progress-track"
                  role="progressbar"
                  aria-valuenow={currentStep + 1}
                  aria-valuemin={1}
                  aria-valuemax={TOTAL_STEPS}
                  aria-label={`Step ${currentStep + 1} of ${TOTAL_STEPS}`}
                >
                  <div
                    className="enterprise-modal-progress-fill"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              <div className="form-actions enquiry-step-actions">
                {currentStep > 0 ? (
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={goBack}
                    disabled={isSubmitting}
                  >
                    Back
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                )}

                {!isLastContentStep ? (
                  <button
                    type="button"
                    className="btn-submit"
                    onClick={goNext}
                    disabled={isSubmitting}
                  >
                    {currentStep === 0 ? 'Get started' : 'Continue'}
                  </button>
                ) : (
                  <button type="submit" className="btn-submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit enquiry'}
                  </button>
                )}
              </div>

              {/* This form collects a name, email and company, so the privacy
                  information has to be reachable from the form itself rather
                  than only from inside an account. New tab deliberately: an
                  in-app navigation unmounts the modal and loses the answers. */}
              <p className="enquiry-privacy-note">
                We use these details only to respond to your enquiry. See our{' '}
                <a href="/policy/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : modalContent;
};

export default EnterpriseEnquiryModal;
